// src/api/index.ts
import { cachedFetch, invalidateCache } from '~/utils/issues/requestCache'

export interface SessionPlanVersion {
  version: number
  content: string
  created_at: string
  created_by?: string
  change_summary?: string
}

export interface SessionContextVersion {
  version: number
  content: string
  created_at: string
  created_by?: string
  change_summary?: string
}

export interface Session {
  session_id: string
  name: string
  issue_key: string
  github_repo: string
  github_org: string
  messages: any[]
  plan: string | null
  context_markdown?: string | null
  issue_details: IssueDetails | null
  confidence_score?: number
  category?: string
  evaluation_result?: any
  team_id?: string
  team_name?: string
  state?: string
  previous_state?: string
  session_type?: 'issue' | 'manual' | 'prototype'
  summary?: string
  description?: string
  // Version fields
  plan_versions?: SessionPlanVersion[]
  current_plan_version?: number
  context_versions?: SessionContextVersion[]
  current_context_version?: number
  created_at: string
}

export interface IssueDetails {
  issue_key: string
  title: string
  description: string
  url: string
  provider: string
  team?: string // Optional, for Linear issues
}

export interface Plan {
  plan: string
}

export interface TeamStats {
  total_issues: number
  total_specs: number
  total_pull_requests: number
  total_prototypes: number
  // Filtered counts for left nav display
  draft_specs: number
  planned_sessions: number
  low_risk_pull_requests: number
  issues_by_status: Record<string, number>
  issues_by_category: Record<string, number>
  processing_progress: number
  started_at?: string
  completed_at?: string
}

export interface Team {
  team_id: string
  name: string
  description?: string
  processing_active: boolean
  processing_events?: any[]
  last_processed_date?: string
  config: {
    provider?: string
    issue_team_key?: string
    linear_team?: string
    github_org: string
    github_repo: string
    query_filter?: string | null
    max_issues?: number
    auto_process_enabled?: boolean
    enable_enhanced_processing?: boolean
  }
  stats?: TeamStats
  created_at: string
  updated_at: string
  created_by?: string
}

export interface SpecPRDVersion {
  version: number
  content: string
  created_at: string
  created_by?: string
  change_summary?: string
}

export interface SpecConfidenceScore {
  overall_confidence: number
  factors: Record<string, number>
  reasoning?: string
  evaluated_at: string
}

export interface Spec {
  hash: string
  spec_key: string
  team_id: string
  original_prompt: string
  title: string
  description?: string
  status: 'draft' | 'active' | 'completed' | 'cancelled'
  prd_versions: SpecPRDVersion[]
  current_prd_version?: number
  session_ids: string[]
  confidence_score?: SpecConfidenceScore
  created_at: string
  updated_at: string
  created_by?: string
  processing_active: boolean
  last_processed_at?: string
}

export interface CreateSpecRequest {
  team_id: string
  original_prompt: string
  title: string
  description?: string
  initial_prd?: string
  created_by?: string
}

export interface Skill {
  hash: string
  slug: string
  name: string
  description?: string
  original_prompt?: string
  team_id?: string
  repository_id?: string
  techdoc_ids: string[]
  created_at: string
  updated_at: string
  created_by?: string
  confidence_score?: number
  techdoc_statuses?: Array<{
    id: string
    status: string
    doc_type: string
  }>
}

export interface CreateSkillRequest {
  name: string
  description?: string
  original_prompt?: string
  team_id?: string
  skills_repository_gid?: string
  created_by?: string
}

export interface PullRequest {
  remote_id: string
  title: string
  description: string
  merged_at: string | null
  merged_into_branch: string
  source_branch_name: string
  url: string
  diff: string
  main_author: {
    username: string
    email: string
  }
  co_authors: Array<{
    username: string
    email: string
  }>
  reviewers: Array<{
    username: string
    email: string
  }>
  issue_keys: string[]
  session_hashes: string[]
  files_changed?: string[]
  num_of_additions: number
  num_of_deletions: number
  num_of_commits: number
  evaluation_result?: {
    pr_number: string
    safety_score: number
    risk_level: string
    risk_factors: string[]
    safety_factors: string[]
    deployment_recommendation: string
    reasoning: string
    code_quality_score: number
    test_coverage_assessment: string
    breaking_change_risk: string
  }
  // Evaluation state for streaming reconnection
  active_evaluation_session_id?: string
  evaluation_status?: 'in_progress' | 'completed' | 'failed' | null
  hash: string
}

export enum IssuesArtifactType {
  Spec = 'SPEC',
  Task = 'TASK',
  Skill = 'SKILL',
}

export const API_BASE = '/api/issues'

// List sessions with pagination, optional search, and optional team filter
export async function listSessions(teamId: string = '', page: number = 1, pageSize: number = 10, search: string = ''): Promise<{ sessions: Session[], total: number, page: number, pageSize: number }> {
  const params = new URLSearchParams({
    page: page.toString(),
    pageSize: pageSize.toString(),
  })

  if (search) {
    params.append('search', search)
  }

  if (teamId) {
    params.append('team_id', teamId)
  }

  return await cachedFetch(`${API_BASE}/session/?${params}`)
}

// Create a new session (issue-backed or manual based on parameters)
export async function createSession(
  github_repo: string,
  github_org: string,
  options: {
    // For issue-backed sessions
    issue_key?: string
    // For manual sessions
    summary?: string
    description?: string
    // Optional fields
    name?: string
    team_id?: string
  } = {},
): Promise<{ session_id: string }> {
  const body: any = { github_repo, github_org }

  // Add optional fields if provided
  if (options.issue_key)
    body.issue_key = options.issue_key
  if (options.summary)
    body.summary = options.summary
  if (options.description)
    body.description = options.description
  if (options.name)
    body.name = options.name
  if (options.team_id)
    body.team_id = options.team_id

  const res = await fetch(`${API_BASE}/session/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  if (!res.ok)
    throw new Error('Failed to create session')

  // Invalidate session lists
  invalidateCache(`sessions?`)

  return await res.json()
}

// Get session details
export async function getSession(session_id: string): Promise<Session> {
  return await cachedFetch(`${API_BASE}/session/${session_id}/`)
}

// Post (update) the implementation plan
export async function postPlan(
  session_id: string,
  plan: string,
): Promise<{ ok: boolean }> {
  const res = await fetch(`${API_BASE}/session/${session_id}/plan/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ plan }),
  })
  if (!res.ok)
    throw new Error('Failed to post plan')

  // Invalidate cache for this session
  invalidateCache(`sessions/${session_id}`)

  return await res.json()
}

// Post the plan to the issue tracking system (user-initiated)
export async function postPlanToLinear(
  session_id: string,
  plan: string,
): Promise<{ ok: boolean, result?: any }> {
  const res = await fetch(`${API_BASE}/session/${session_id}/post_plan/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ plan }),
  })
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}))
    throw new Error(errorData.error || 'Failed to post plan to issue tracker')
  }
  return await res.json()
}

// Stream all events (plan, ai, user, tool_call, tool_result, error) via SSE as JSON with a 'type' field
export function streamAllEvents(
  session_id: string,
  onMessage: (event: { type: string, content: string, index?: number }) => void,
  lastEventIndex: number = 0,
  onStreamEnd?: () => void,
): EventSource {
  const url = lastEventIndex
    ? `${API_BASE}/session/${session_id}/stream/?last_event_index=${lastEventIndex}`
    : `${API_BASE}/session/${session_id}/stream/`
  const es = new EventSource(url)
  es.onmessage = (event) => {
    try {
      const data = JSON.parse(event.data)
      onMessage(data)
    }
    catch {}
  }

  es.onerror = (error) => {
    console.error('EventSource error for session', session_id, ':', error)
    // Check if this is a natural stream end (readyState 2 = CLOSED)
    if (es.readyState === EventSource.CLOSED) {
      console.log('Stream naturally ended for session', session_id)
      if (onStreamEnd) {
        onStreamEnd()
      }
    }
  }

  return es
}

// Generic streaming function for any streamable object (Sessions, PullRequests, etc.)
export function streamEvents(
  stream_id: string,
  onMessage: (event: { type: string, content: string, index?: number }) => void,
  lastEventIndex: number = 0,
  onStreamEnd?: () => void,
): EventSource {
  const url = lastEventIndex
    ? `${API_BASE}/streams/${stream_id}/stream/?last_event_index=${lastEventIndex}`
    : `${API_BASE}/streams/${stream_id}/stream/`

  const es = new EventSource(url)
  es.onmessage = (event) => {
    try {
      const data = JSON.parse(event.data)
      onMessage(data)
    }
    catch (e) {
      console.error('Failed to parse streaming event:', e)
    }
  }

  es.onerror = (error) => {
    console.error('EventSource error for stream', stream_id, ':', error)
    // Check if this is a natural stream end (readyState 2 = CLOSED)
    if (es.readyState === EventSource.CLOSED) {
      console.log('Stream naturally ended for', stream_id)
      if (onStreamEnd) {
        onStreamEnd()
      }
    }
  }

  return es
}

// Send a user chat message
export async function sendMessage(
  session_id: string,
  message: string,
): Promise<{ ok: boolean }> {
  const res = await fetch(`${API_BASE}/session/${session_id}/chat/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ message }),
  })
  if (!res.ok)
    throw new Error('Failed to send message')
  return await res.json()
}

// Update session name
export async function updateSessionName(
  session_id: string,
  name: string,
): Promise<{ ok: boolean }> {
  const res = await fetch(`${API_BASE}/session/${session_id}/rename/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name }),
  })
  if (!res.ok)
    throw new Error('Failed to rename session')
  return await res.json()
}

// Session Version API functions
export async function createPlanVersion(
  session_id: string,
  content: string,
  change_summary?: string,
  make_current: boolean = true,
  created_by?: string,
): Promise<{ success: boolean, version: SessionPlanVersion, message: string }> {
  const res = await fetch(`${API_BASE}/session/${session_id}/plan/versions/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      content,
      change_summary,
      make_current,
      created_by,
    }),
  })
  if (!res.ok)
    throw new Error('Failed to create plan version')
  return await res.json()
}

export async function getPlanVersions(
  session_id: string,
): Promise<{ success: boolean, versions: SessionPlanVersion[], current_version?: number }> {
  const res = await fetch(`${API_BASE}/session/${session_id}/plan/versions/`)
  if (!res.ok)
    throw new Error('Failed to fetch plan versions')
  return await res.json()
}

export async function makePlanVersionCurrent(
  session_id: string,
  version_number: number,
): Promise<{ success: boolean, message: string, current_version: number }> {
  const res = await fetch(`${API_BASE}/session/${session_id}/plan/versions/${version_number}/current/`, {
    method: 'PUT',
  })
  if (!res.ok)
    throw new Error('Failed to make plan version current')
  return await res.json()
}

export async function createContextVersion(
  session_id: string,
  content: string,
  change_summary?: string,
  make_current: boolean = true,
  created_by?: string,
): Promise<{ success: boolean, version: SessionContextVersion, message: string }> {
  const res = await fetch(`${API_BASE}/session/${session_id}/context/versions/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      content,
      change_summary,
      make_current,
      created_by,
    }),
  })
  if (!res.ok)
    throw new Error('Failed to create context version')
  return await res.json()
}

export async function getContextVersions(
  session_id: string,
): Promise<{ success: boolean, versions: SessionContextVersion[], current_version?: number }> {
  const res = await fetch(`${API_BASE}/session/${session_id}/context/versions/`)
  if (!res.ok)
    throw new Error('Failed to fetch context versions')
  return await res.json()
}

export async function makeContextVersionCurrent(
  session_id: string,
  version_number: number,
): Promise<{ success: boolean, message: string, current_version: number }> {
  const res = await fetch(`${API_BASE}/session/${session_id}/context/versions/${version_number}/current/`, {
    method: 'PUT',
  })
  if (!res.ok)
    throw new Error('Failed to make context version current')
  return await res.json()
}

export async function deleteAllVersions(
  session_id: string,
): Promise<{ success: boolean, message: string }> {
  const res = await fetch(`${API_BASE}/session/${session_id}/versions/`, {
    method: 'DELETE',
  })
  if (!res.ok)
    throw new Error('Failed to delete all versions')
  return await res.json()
}

// List all teams
export async function listTeams(): Promise<{ teams: Team[], total: number }> {
  return await cachedFetch(`${API_BASE}/teamspace/`)
}

// Search teams by name and description
export async function searchTeams(search: string): Promise<{ teams: Team[], total: number }> {
  const params = new URLSearchParams()
  if (search) {
    params.append('search', search)
  }

  const res = await fetch(`${API_BASE}/teamspace/search/?${params}`)
  if (!res.ok)
    throw new Error('Failed to search teams')
  return await res.json()
}

// Add convenience function for getTeams (alias for listTeams)
export const getTeams = listTeams

// Get a single team by ID
export async function getTeam(teamId: string): Promise<Team> {
  return await cachedFetch(`${API_BASE}/teamspace/${teamId}/`)
}

// Spec API functions
export async function listSpecs(teamId?: string): Promise<{ specs: Spec[], total: number }> {
  const params = new URLSearchParams()
  if (teamId) {
    params.append('team_id', teamId)
  }

  return await cachedFetch(`${API_BASE}/specs/?${params}`)
}

export async function getSpecsByTeam(
  teamId: string,
  page: number = 1,
  pageSize: number = 10,
  search?: string,
): Promise<{ specs: Spec[], total: number }> {
  const params = new URLSearchParams()
  params.append('team_id', teamId)
  params.append('page', page.toString())
  params.append('page_size', pageSize.toString())
  if (search) {
    params.append('search', search)
  }

  const res = await fetch(`${API_BASE}/specs/?${params}`)
  if (!res.ok)
    throw new Error('Failed to fetch specs')
  return await res.json()
}

export async function createSpec(specData: CreateSpecRequest): Promise<Spec> {
  const res = await fetch(`${API_BASE}/specs/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(specData),
  })
  if (!res.ok)
    throw new Error('Failed to create spec')

  // Invalidate spec lists
  invalidateCache(`specs?`)
  if (specData.team_id) {
    invalidateCache(`teams/${specData.team_id}`)
  }

  return await res.json()
}

export async function getSpec(specHash: string): Promise<Spec> {
  return await cachedFetch(`${API_BASE}/specs/${specHash}/`)
}

export async function updateSpec(specHash: string, updates: Partial<Spec>): Promise<Spec> {
  const res = await fetch(`${API_BASE}/specs/${specHash}/`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(updates),
  })
  if (!res.ok)
    throw new Error('Failed to update spec')

  // Invalidate cache for this spec
  invalidateCache(`specs/${specHash}`)
  invalidateCache(`specs?`) // Invalidate list queries too

  return await res.json()
}

export async function deleteSpec(specHash: string): Promise<void> {
  const res = await fetch(`${API_BASE}/specs/${specHash}/`, {
    method: 'DELETE',
  })
  if (!res.ok)
    throw new Error('Failed to delete spec')
}

export async function getSpecStats(specHash: string): Promise<any> {
  const res = await fetch(`${API_BASE}/specs/${specHash}/stats/`)
  if (!res.ok)
    throw new Error('Failed to fetch spec stats')
  return await res.json()
}

export async function addPRDVersion(specHash: string, versionData: {
  content: string
  change_summary?: string
  make_current?: boolean
  created_by?: string
}): Promise<SpecPRDVersion> {
  const res = await fetch(`${API_BASE}/specs/${specHash}/prd-versions/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(versionData),
  })
  if (!res.ok)
    throw new Error('Failed to add PRD version')
  return await res.json()
}

// === SKILL API ===

export async function listSkills(params: {
  teamId?: string
  repositoryId?: string
  ownedBy?: 'repository' | 'team' | 'organization' | 'user'
  page?: number
  pageSize?: number
  search?: string
  status?: string
  sortBy?: 'confidence' | 'created'
} = {}): Promise<{ skills: Skill[], total: number }> {
  const searchParams = new URLSearchParams()
  if (params.teamId)
    searchParams.append('team_id', params.teamId)
  if (params.repositoryId)
    searchParams.append('repository_id', params.repositoryId)
  if (params.ownedBy)
    searchParams.append('owned_by', params.ownedBy)
  if (params.page)
    searchParams.append('page', params.page.toString())
  if (params.pageSize)
    searchParams.append('page_size', params.pageSize.toString())
  if (params.search)
    searchParams.append('search', params.search)
  if (params.status)
    searchParams.append('status', params.status)
  if (params.sortBy)
    searchParams.append('sort_by', params.sortBy)

  return await cachedFetch(`${API_BASE}/skills/?${searchParams.toString()}`)
}

export async function getSkill(skillHash: string): Promise<Skill> {
  return await cachedFetch(`${API_BASE}/skills/${skillHash}/`)
}

export async function createSkill(skillData: CreateSkillRequest): Promise<Skill> {
  const res = await fetch(`${API_BASE}/skills/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(skillData),
  })
  if (!res.ok)
    throw new Error('Failed to create skill')

  // Invalidate cache
  invalidateCache(`skills?`)

  return await res.json()
}

export async function updateSkill(skillHash: string, updates: Partial<Skill>): Promise<Skill> {
  const res = await fetch(`${API_BASE}/skills/${skillHash}/`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(updates),
  })
  if (!res.ok)
    throw new Error('Failed to update skill')

  // Invalidate cache for this skill
  invalidateCache(`skills/${skillHash}`)
  invalidateCache(`skills?`) // Invalidate list queries too

  return await res.json()
}

export async function deleteSkill(skillHash: string): Promise<void> {
  const res = await fetch(`${API_BASE}/skills/${skillHash}/`, {
    method: 'DELETE',
  })
  if (!res.ok)
    throw new Error('Failed to delete skill')

  // Invalidate cache
  invalidateCache(`skills/${skillHash}`)
  invalidateCache(`skills?`)
}

export async function getSkillTechDocs(skillHash: string): Promise<any[]> {
  return await cachedFetch(`${API_BASE}/skills/${skillHash}/techdocs/`)
}

export async function getPRDVersions(specHash: string): Promise<{ versions: SpecPRDVersion[] }> {
  const res = await fetch(`${API_BASE}/specs/${specHash}/prd-versions/`)
  if (!res.ok)
    throw new Error('Failed to fetch PRD versions')
  return await res.json()
}

export async function setCurrentPRDVersion(specHash: string, versionNumber: number): Promise<Spec> {
  const res = await fetch(`${API_BASE}/specs/${specHash}/current-prd-version/`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ version_number: versionNumber }),
  })
  if (!res.ok)
    throw new Error('Failed to set current PRD version')
  return await res.json()
}

export async function makeVersionCurrent(specHash: string, versionNumber: number): Promise<Spec> {
  const res = await fetch(`${API_BASE}/specs/${specHash}/current-prd-version/`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      version_number: versionNumber,
      trigger_tools: true, // This triggers evaluation and breakdown
    }),
  })
  if (!res.ok)
    throw new Error('Failed to make version current')
  return await res.json()
}

// Prototype API functions
export async function createPrototype(prototypeData: {
  name: string
  description: string
  repo?: string
  issue_key?: string
  team_id?: string
}): Promise<{ session_id: string, session: Session }> {
  const res = await fetch(`${API_BASE}/prototypes/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(prototypeData),
  })
  if (!res.ok)
    throw new Error('Failed to create prototype')
  return await res.json()
}

export async function getPrototypeHTML(sessionId: string): Promise<string> {
  const res = await fetch(`${API_BASE}/prototypes/${sessionId}/html/`)
  if (!res.ok)
    throw new Error('Failed to get prototype HTML')
  return await res.text()
}

// Note: generatePrototypeName is no longer needed as the backend now handles name generation automatically

export async function linkSessionToSpec(specHash: string, sessionId: string): Promise<{ success: boolean, spec: Spec }> {
  const res = await fetch(`${API_BASE}/specs/${specHash}/session/${sessionId}/`, {
    method: 'POST',
  })
  if (!res.ok)
    throw new Error('Failed to link session to spec')
  return await res.json()
}

export async function unlinkSessionFromSpec(specHash: string, sessionId: string): Promise<{ success: boolean, spec: Spec }> {
  const res = await fetch(`${API_BASE}/specs/${specHash}/session/${sessionId}/`, {
    method: 'DELETE',
  })
  if (!res.ok)
    throw new Error('Failed to unlink session from spec')
  return await res.json()
}

export async function evaluateSpecConfidence(specHash: string): Promise<Spec> {
  const res = await fetch(`${API_BASE}/specs/${specHash}/evaluate/`, {
    method: 'POST',
  })
  if (!res.ok)
    throw new Error('Failed to evaluate spec confidence')
  return await res.json()
}

// Validate if an issue exists in the remote system
export async function validateIssue(
  issue_key: string,
): Promise<{
  exists: boolean
  issue?: {
    key: string
    title: string
    description: string
  }
  error?: string
}> {
  const res = await fetch(`${API_BASE}/validate-issue/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ issue_key }),
  })
  if (!res.ok)
    throw new Error('Failed to validate issue')
  return await res.json()
}

// Validate if a team query is valid and can execute successfully
export async function validateTeamQuery(
  query_filter: string,
  linear_team: string,
): Promise<{
  valid: boolean
  results_count?: number
  error?: string
  suggestion?: string
}> {
  const res = await fetch(`${API_BASE}/validate-team-query/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ query_filter, linear_team }),
  })
  if (!res.ok)
    throw new Error('Failed to validate team query')
  return await res.json()
}

// Evaluate issue for a session (uses cache if available)
export async function evaluateSessionIssue(
  session_id: string,
): Promise<{
  session_id: string
  issue_key: string
  evaluation: any
  timestamp: string | null
  cached?: boolean
}> {
  const res = await fetch(`${API_BASE}/session/${session_id}/evaluate/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  })
  if (!res.ok)
    throw new Error('Failed to evaluate session issue')
  return await res.json()
}

// Get evaluation status without triggering new evaluation
export async function getEvaluationStatus(
  session_id: string,
): Promise<{
  session_id: string
  issue_key: string
  has_evaluation: boolean
  evaluation: any
  timestamp: string | null
}> {
  return await cachedFetch(`${API_BASE}/session/${session_id}/evaluation/`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  })
}

// Force re-evaluation (bypass cache)
export async function forceEvaluateSessionIssue(
  session_id: string,
): Promise<{
  session_id: string
  issue_key: string
  evaluation: any
  timestamp: string | null
  cached?: boolean
}> {
  const res = await fetch(`${API_BASE}/session/${session_id}/evaluate/force/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  })
  if (!res.ok)
    throw new Error('Failed to force evaluate session issue')
  return await res.json()
}

// Reset session for re-planning (clear plan, evaluation, and messages)
export async function resetSession(
  session_id: string,
): Promise<{
  success: boolean
  message: string
  session_id: string
}> {
  const res = await fetch(`${API_BASE}/session/${session_id}/reset/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  })
  if (!res.ok)
    throw new Error('Failed to reset session')
  return await res.json()
}

// Delete session permanently
export async function deleteSession(
  session_id: string,
): Promise<{
  success: boolean
  message: string
}> {
  const res = await fetch(`${API_BASE}/session/${session_id}/`, {
    method: 'DELETE',
  })
  if (!res.ok)
    throw new Error('Failed to delete session')
  return await res.json()
}

// Send session to Okteto agent
export async function sendSessionToAgent(
  session_id: string,
): Promise<{
  success: boolean
  agent_id?: string
  branch_name?: string
  message: string
  error?: string
}> {
  const res = await fetch(`${API_BASE}/session/${session_id}/send-to-agent/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  })
  if (!res.ok)
    throw new Error('Failed to send session to agent')
  return await res.json()
}

// Cancel agent for a session
export async function cancelAgent(
  session_id: string,
): Promise<{
  success: boolean
  message: string
  error?: string
}> {
  const res = await fetch(`${API_BASE}/session/${session_id}/cancel-agent/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  })
  if (!res.ok)
    throw new Error('Failed to cancel agent')
  return await res.json()
}

// List pull requests with pagination, optional search, and optional status filter
export async function listPullRequests(page: number = 1, pageSize: number = 10, search: string = '', status: string = '', teamId?: string): Promise<{ pull_requests: PullRequest[], total: number, page: number, page_size: number }> {
  const params = new URLSearchParams({
    page: page.toString(),
    page_size: pageSize.toString(),
  })

  if (search) {
    params.append('search', search)
  }

  if (status) {
    params.append('status', status)
  }

  if (teamId) {
    params.append('team_id', teamId)
  }

  const res = await fetch(`${API_BASE}/pull_request/?${params}`)
  if (!res.ok)
    throw new Error('Failed to fetch pull requests')
  return await res.json()
}

// Delete pull request permanently
export async function deletePullRequest(
  prId: string,
): Promise<{
  success: boolean
}> {
  const res = await fetch(`${API_BASE}/pull_request/${prId}/`, {
    method: 'DELETE',
  })
  if (!res.ok)
    throw new Error('Failed to delete pull request')
  return await res.json()
}

// Analyze work request to determine if it should be a Spec or Session
export async function analyzeWorkRequest(userInput: string, teamId?: string): Promise<{
  decision: 'spec' | 'session'
  summary: string
  reasoning: string
  confidence: number
}> {
  const res = await fetch(`${API_BASE}/analyze-work-request/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      user_input: userInput,
      team_id: teamId,
    }),
  })
  if (!res.ok)
    throw new Error('Failed to analyze work request')
  return await res.json()
}

// Helper function to ensure trailing slash unless path has query parameters
function ensureTrailingSlash(path: string): string {
  if (path.includes('?')) {
    // If there's a query parameter, ensure trailing slash before the ?
    const [pathname, query] = path.split('?')
    const normalizedPath = pathname.endsWith('/') ? pathname : `${pathname}/`
    return `${normalizedPath}?${query}`
  }
  // If no query parameters, just ensure trailing slash
  return path.endsWith('/') ? path : `${path}/`
}

// Generic API helper for workspace endpoints
export const api = {
  get: async (path: string) => {
    const normalizedPath = ensureTrailingSlash(path)
    const data = await cachedFetch(`${API_BASE}${normalizedPath}`)
    return { data }
  },
  post: async (path: string, data?: any) => {
    const normalizedPath = ensureTrailingSlash(path)
    const res = await fetch(`${API_BASE}${normalizedPath}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: data ? JSON.stringify(data) : undefined,
    })
    if (!res.ok)
      throw new Error(`Failed to POST ${path}`)
    return { data: await res.json() }
  },
  put: async (path: string, data?: any) => {
    const normalizedPath = ensureTrailingSlash(path)
    const res = await fetch(`${API_BASE}${normalizedPath}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: data ? JSON.stringify(data) : undefined,
    })
    if (!res.ok)
      throw new Error(`Failed to PUT ${path}`)
    return { data: await res.json() }
  },
  delete: async (path: string) => {
    const normalizedPath = ensureTrailingSlash(path)
    const res = await fetch(`${API_BASE}${normalizedPath}`, {
      method: 'DELETE',
    })
    if (!res.ok)
      throw new Error(`Failed to DELETE ${path}`)
    return { data: await res.json() }
  },

  patch: async (path: string) => {
    const normalizedPath = ensureTrailingSlash(path)
    const res = await fetch(`${API_BASE}${normalizedPath}`, {
      method: 'PATCH',
    })
    if (!res.ok)
      throw new Error(`Failed to PATCH ${path}`)
    return { data: await res.json() }
  },
}

// ===============================================================================
// UNIFIED ORCHESTRATION API - Phase 8 Implementation
// ===============================================================================

/**
 * Generic orchestration function for all StreamableObject types.
 * Replaces all domain-specific chat/orchestration endpoints.
 */
export function orchestrateUniversal(
  domainType: string,
  domainId: string,
  message?: string,
  options: Record<string, any> = {},
): Promise<{ success: boolean, stream_id: string, domain_type: string, display_name: string }> {
  return fetch(`${API_BASE}/${domainType}/${domainId}/orchestrate/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ message, ...options }),
  }).then((r) => {
    if (!r.ok)
      throw new Error(`Orchestration failed for ${domainType}/${domainId}`)
    return r.json()
  })
}

/**
 * Universal streaming function for all orchestrators.
 * Works with the unified streaming endpoint that handles all StreamableObject types.
 */
export function streamUniversalEvents(
  streamId: string, // Already in format: domain_type_id (e.g., session_abc123, spec_xyz789)
  onMessage: (event: any) => void,
  lastEventIndex: number = 0,
): EventSource {
  const url = lastEventIndex
    ? `${API_BASE}/streams/${streamId}/stream/?last_event_index=${lastEventIndex}`
    : `${API_BASE}/streams/${streamId}/stream/`

  const es = new EventSource(url)
  es.onmessage = (event) => {
    try {
      const parsed = JSON.parse(event.data)
      onMessage(parsed)
    }
    catch (e) {
      console.error('Failed to parse universal stream event:', e)
    }
  }
  es.onerror = (error) => {
    console.error('Universal stream error:', error)
  }
  return es
}

/**
 * Stop orchestration for any domain type.
 */
export function stopUniversalOrchestration(
  domainType: string,
  domainId: string,
): Promise<{ success: boolean, message: string }> {
  return fetch(`${API_BASE}/${domainType}/${domainId}/stop/`, {
    method: 'POST',
  }).then((r) => {
    if (!r.ok)
      throw new Error(`Failed to stop orchestration for ${domainType}/${domainId}`)
    return r.json()
  })
}

/**
 * Convenience wrapper for chat interactions.
 * Routes to the unified orchestration endpoint with a message.
 */
export function chatUniversal(
  domainType: string,
  domainId: string,
  message: string,
  options: Record<string, any> = {},
): Promise<{ success: boolean, stream_id: string, domain_type: string, display_name: string }> {
  return orchestrateUniversal(domainType, domainId, message, options)
}
